import { useTexts } from "hooks/useTexts";
import React, { useEffect, useState } from "react";
import LoadingPlaceholder from "ui/LoadingPlaceholder";

export const avatarOptions = {
  topType: [
    "NoHair",
    "Eyepatch",
    "Hat",
    "Hijab",
    "Turban",
    "WinterHat1",
    "WinterHat2",
    "WinterHat3",
    "WinterHat4",
    "LongHairBigHair",
    "LongHairBob",
    "LongHairBun",
    "LongHairCurly",
    "LongHairCurvy",
    "LongHairDreads",
    "LongHairFrida",
    "LongHairFro",
    "LongHairFroBand",
    "LongHairNotTooLong",
    "LongHairShavedSides",
    "LongHairMiaWallace",
    "LongHairStraight",
    "LongHairStraight2",
    "LongHairStraightStrand",
    "ShortHairDreads01",
    "ShortHairDreads02",
    "ShortHairFrizzle",
    "ShortHairShaggyMullet",
    "ShortHairShortCurly",
    "ShortHairShortFlat",
    "ShortHairShortRound",
    "ShortHairShortWaved",
    "ShortHairSides",
    "ShortHairTheCaesar",
    "ShortHairTheCaesarSidePart",
  ],
  accessoriesType: [
    "Blank",
    "Kurt",
    "Prescription01",
    "Prescription02",
    "Round",
    "Sunglasses",
    "Wayfarers",
  ],
  hairColor: [
    "Auburn",
    "Black",
    "Blonde",
    "BlondeGolden",
    "Brown",
    "BrownDark",
    "PastelPink",
    "Blue",
    "Platinum",
    "Red",
    "SilverGray",
  ],
  hatColor: [
    "Black",
    "Blue01",
    "Blue02",
    "Blue03",
    "Gray01",
    "Gray02",
    "Heather",
    "PastelBlue",
    "PastelGreen",
    "PastelOrange",
    "PastelRed",
    "PastelYellow",
    "Pink",
    "Red",
    "White",
  ],
  facialHairType: [
    "Blank",
    "BeardMedium",
    "BeardLight",
    "BeardMajestic",
    "MoustacheFancy",
    "MoustacheMagnum",
  ],
  facialHairColor: [
    "Auburn",
    "Black",
    "Blonde",
    "BlondeGolden",
    "Brown",
    "BrownDark",
    "Platinum",
    "Red",
  ],
  clotheType: [
    "BlazerShirt",
    "BlazerSweater",
    "CollarSweater",
    "GraphicShirt",
    "Hoodie",
    "Overall",
    "ShirtCrewNeck",
    "ShirtScoopNeck",
    "ShirtVNeck",
  ],
  clotheColor: [
    "Black",
    "Blue01",
    "Blue02",
    "Blue03",
    "Gray01",
    "Gray02",
    "Heather",
    "PastelBlue",
    "PastelGreen",
    "PastelOrange",
    "PastelRed",
    "PastelYellow",
    "Pink",
    "Red",
    "White",
  ],
  graphicType: [
    "Bat",
    "Cumbia",
    "Deer",
    "Diamond",
    "Hola",
    "Pizza",
    "Resist",
    "Selena",
    "Bear",
    "SkullOutline",
    "Skull",
  ],
  eyeType: [
    "Close",
    "Cry",
    "Default",
    "Dizzy",
    "EyeRoll",
    "Happy",
    "Hearts",
    "Side",
    "Squint",
    "Surprised",
    "Wink",
    "WinkWacky",
  ],
  eyebrowType: [
    "Angry",
    "AngryNatural",
    "Default",
    "DefaultNatural",
    "FlatNatural",
    "RaisedExcited",
    "RaisedExcitedNatural",
    "SadConcerned",
    "SadConcernedNatural",
    "UnibrowNatural",
    "UpDown",
    "UpDownNatural",
  ],
  mouthType: [
    "Concerned",
    "Default",
    "Disbelief",
    "Eating",
    "Grimace",
    "Sad",
    "ScreamOpen",
    "Serious",
    "Smile",
    "Tongue",
    "Twinkle",
    "Vomit",
  ],
  skinColor: [
    "Tanned",
    "Yellow",
    "Pale",
    "Light",
    "Brown",
    "DarkBrown",
    "Black",
  ],
} as const;

export type AvatarOptionKey = keyof typeof avatarOptions;

interface AvatarEditorProps {
  avatarUrl?: string;
  onChangeAvatar?: (url: string) => void;
  className?: string;
  style?: React.CSSProperties;
}

const defaultSelections: Record<AvatarOptionKey, string> = {
  topType: "LongHairMiaWallace",
  accessoriesType: "Prescription02",
  hairColor: "BrownDark",
  hatColor: "Black",
  facialHairType: "Blank",
  facialHairColor: "Auburn",
  clotheType: "Hoodie",
  clotheColor: "PastelBlue",
  graphicType: "Bat",
  eyeType: "Happy",
  eyebrowType: "Default",
  mouthType: "Smile",
  skinColor: "Light",
};

const optionLabelDefaults: Record<AvatarOptionKey, string> = {
  topType: "Причёска",
  accessoriesType: "Аксессуары",
  hairColor: "Цвет волос",
  hatColor: "Цвет шапки",
  facialHairType: "Растительность на лице",
  facialHairColor: "Цвет бороды/усов",
  clotheType: "Одежда",
  clotheColor: "Цвет одежды",
  graphicType: "Принт",
  eyeType: "Глаза",
  eyebrowType: "Брови",
  mouthType: "Рот",
  skinColor: "Цвет кожи",
};

const optionValueDefaults: Record<string, string> = {
  // topType
  NoHair: "Лысый",
  Eyepatch: "Повязка на глаз",
  Hat: "Шляпа",
  Hijab: "Хиджаб",
  Turban: "Тюрбан",
  WinterHat1: "Зимняя шапка 1",
  WinterHat2: "Зимняя шапка 2",
  WinterHat3: "Зимняя шапка 3",
  WinterHat4: "Зимняя шапка 4",
  LongHairBigHair: "Длинные пышные",
  LongHairBob: "Каре",
  LongHairBun: "Пучок",
  LongHairCurly: "Длинные кудрявые",
  LongHairCurvy: "Длинные волнистые",
  LongHairDreads: "Длинные дреды",
  LongHairFrida: "Фрида",
  LongHairFro: "Афро длинные",
  LongHairFroBand: "Афро с повязкой",
  LongHairNotTooLong: "Не слишком длинные",
  LongHairShavedSides: "Выбритые виски",
  LongHairMiaWallace: "Миа Уоллес",
  LongHairStraight: "Длинные прямые",
  LongHairStraight2: "Длинные прямые 2",
  LongHairStraightStrand: "Длинные с прядью",
  ShortHairDreads01: "Короткие дреды 1",
  ShortHairDreads02: "Короткие дреды 2",
  ShortHairFrizzle: "Короткие вьющиеся",
  ShortHairShaggyMullet: "Маллет",
  ShortHairShortCurly: "Короткие кудри",
  ShortHairShortFlat: "Короткие гладкие",
  ShortHairShortRound: "Короткие округлые",
  ShortHairShortWaved: "Короткие волнистые",
  ShortHairSides: "Короткие по бокам",
  ShortHairTheCaesar: "Цезарь",
  ShortHairTheCaesarSidePart: "Цезарь с пробором",
  // accessoriesType
  Blank: "Нет",
  Kurt: "Курт",
  Prescription01: "Очки 1",
  Prescription02: "Очки 2",
  Round: "Круглые",
  Sunglasses: "Солнцезащитные",
  Wayfarers: "Вайфареры",
  // hairColor / facialHairColor
  Auburn: "Каштановый",
  Black: "Чёрный",
  Blonde: "Блонд",
  BlondeGolden: "Золотистый блонд",
  Brown: "Коричневый",
  BrownDark: "Тёмно-коричневый",
  PastelPink: "Пастельно-розовый",
  Blue: "Синий",
  Platinum: "Платиновый",
  Red: "Рыжий",
  SilverGray: "Серебристо-серый",
  // hatColor / clotheColor
  Blue01: "Синий 1",
  Blue02: "Синий 2",
  Blue03: "Синий 3",
  Gray01: "Серый 1",
  Gray02: "Серый 2",
  Heather: "Меланж",
  PastelBlue: "Пастельно-голубой",
  PastelGreen: "Пастельно-зелёный",
  PastelOrange: "Пастельно-оранжевый",
  PastelRed: "Пастельно-красный",
  PastelYellow: "Пастельно-жёлтый",
  Pink: "Розовый",
  White: "Белый",
  // facialHairType
  BeardMedium: "Средняя борода",
  BeardLight: "Лёгкая борода",
  BeardMajestic: "Густая борода",
  MoustacheFancy: "Изящные усы",
  MoustacheMagnum: "Усы Магнум",
  // clotheType
  BlazerShirt: "Блейзер с рубашкой",
  BlazerSweater: "Блейзер со свитером",
  CollarSweater: "Свитер с воротником",
  GraphicShirt: "Футболка с принтом",
  Hoodie: "Худи",
  Overall: "Комбинезон",
  ShirtCrewNeck: "Футболка с круглым вырезом",
  ShirtScoopNeck: "Футболка с глубоким вырезом",
  ShirtVNeck: "Футболка с V-вырезом",
  // graphicType
  Bat: "Летучая мышь",
  Cumbia: "Кумбия",
  Deer: "Олень",
  Diamond: "Бриллиант",
  Hola: "Привет",
  Pizza: "Пицца",
  Resist: "Сопротивление",
  Selena: "Селена",
  Bear: "Медведь",
  SkullOutline: "Контур черепа",
  Skull: "Череп",
  // eyeType
  Close: "Закрытые",
  Cry: "Плач",
  Default: "Обычные",
  Dizzy: "Головокружение",
  EyeRoll: "Закатывание глаз",
  Happy: "Счастливые",
  Hearts: "Сердечки",
  Side: "В сторону",
  Squint: "Прищур",
  Surprised: "Удивлённые",
  Wink: "Подмигивание",
  WinkWacky: "Шальное подмигивание",
  // eyebrowType
  Angry: "Злые",
  AngryNatural: "Злые естественные",
  DefaultNatural: "Обычные естественные",
  FlatNatural: "Плоские естественные",
  RaisedExcited: "Приподнятые",
  RaisedExcitedNatural: "Приподнятые естественные",
  SadConcerned: "Грустные",
  SadConcernedNatural: "Грустные естественные",
  UnibrowNatural: "Монобровь",
  UpDown: "Вверх-вниз",
  UpDownNatural: "Вверх-вниз естественные",
  // mouthType
  Concerned: "Обеспокоенный",
  Disbelief: "Недоверие",
  Eating: "Жуёт",
  Grimace: "Гримаса",
  Sad: "Грустный",
  ScreamOpen: "Крик",
  Serious: "Серьёзный",
  Smile: "Улыбка",
  Tongue: "Язык",
  Twinkle: "Ухмылка",
  Vomit: "Тошнота",
  // skinColor
  Tanned: "Загорелая",
  Yellow: "Жёлтая",
  Pale: "Бледная",
  Light: "Светлая",
  DarkBrown: "Тёмно-коричневая",
};

export function randomAvatarOptions(): Record<AvatarOptionKey, string> {
  const result = {} as Record<AvatarOptionKey, string>;
  for (const key of Object.keys(avatarOptions) as AvatarOptionKey[]) {
    const values = avatarOptions[key];
    result[key] = values[Math.floor(Math.random() * values.length)];
  }

  return result;
}

export function avatarOptionsToUrl(
  options: Record<AvatarOptionKey, string>,
  avatarStyle = "Transparent",
): string {
  const params = new URLSearchParams({ avatarStyle });
  for (const [key, value] of Object.entries(options)) {
    params.set(key, value);
  }

  return `https://avataaars.io/?${params.toString()}`;
}

export function parseAvatarUrl(url: string): Record<AvatarOptionKey, string> {
  const params = new URLSearchParams(
    url.includes("?") ? url.split("?")[1] : url,
  );
  const result = { ...defaultSelections };
  for (const key of Object.keys(avatarOptions) as AvatarOptionKey[]) {
    const value = params.get(key);

    if (value && (avatarOptions[key] as readonly string[]).includes(value)) {
      result[key] = value;
    }
  }

  return result;
}

const AvatarEditor: React.FC<AvatarEditorProps> = ({
  avatarUrl,
  onChangeAvatar,
  className,
  style,
}) => {
  const { txt } = useTexts("avatar_editor");

  const [selections, setSelections] = useState<Record<AvatarOptionKey, string>>(
    avatarUrl ? parseAvatarUrl(avatarUrl) : defaultSelections,
  );
  const avatarSrc = avatarOptionsToUrl(selections);

  // avataaars.io рендерит по запросу — без этого смена опции выглядит так,
  // будто ничего не произошло, пока не подгрузится новая картинка.
  const [isAvatarLoading, setIsAvatarLoading] = useState(true);

  useEffect(() => {
    setIsAvatarLoading(true);
  }, [avatarSrc]);

  useEffect(() => {
    setSelections(avatarUrl ? parseAvatarUrl(avatarUrl) : defaultSelections);
  }, [avatarUrl]);

  const updateSelections = (next: Record<AvatarOptionKey, string>) => {
    setSelections(next);
    onChangeAvatar?.(avatarOptionsToUrl(next));
  };

  const handleChange = (key: AvatarOptionKey, value: string) => {
    const next = { ...selections, [key]: value };
    updateSelections(next);
  };

  const handleRandomize = () => {
    updateSelections(randomAvatarOptions());
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 16,
        ...style,
      }}
      className={className}
    >
      {/* Превью берётся с avataaars.io — того же сервиса, что рендерит
          сохранённый аватар везде в приложении. */}
      <div style={{ position: "relative", width: 200, height: 200 }}>
        <img
          src={avatarSrc}
          alt=""
          style={{
            width: 200,
            height: 200,
            opacity: isAvatarLoading ? 0.3 : 1,
            transition: "opacity 0.15s",
          }}
          onLoad={() => setIsAvatarLoading(false)}
          onError={() => setIsAvatarLoading(false)}
        />
        {isAvatarLoading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LoadingPlaceholder />
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={handleRandomize}
        style={{ padding: "8px 16px", fontSize: 14, cursor: "pointer" }}
      >
        {txt("random_button", "Случайный аватар")}
      </button>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
          width: "100%",
          maxWidth: 500,
        }}
      >
        {(Object.keys(avatarOptions) as AvatarOptionKey[]).map((key) => (
          <label
            key={key}
            style={{ display: "flex", flexDirection: "column", fontSize: 13 }}
          >
            {txt("label_" + key, optionLabelDefaults[key])}
            <select
              value={selections[key]}
              onChange={(e) => handleChange(key, e.target.value)}
              style={{ marginTop: 2, padding: "4px 6px" }}
            >
              {avatarOptions[key].map((val) => (
                <option
                  key={val}
                  value={val}
                >
                  {txt("opt_" + val, optionValueDefaults[val] || val)}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>
    </div>
  );
};

export default AvatarEditor;
